import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

export interface PWAState {
  isInstalled: boolean;
  canInstall: boolean;
  installationDate: Date | null;
  isStandalone: boolean;
  deferredPrompt: any | null;
  serviceWorkerUpdateAvailable: boolean;
}

export interface DeviceInfo {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
  isTablet: boolean;
  osVersion: string;
  browser: string;
  canInstallPWA: boolean;
  canInstallMobileApp: boolean;
  mobileAppCompatible: boolean;
  deviceType: 'ios' | 'android' | 'desktop' | 'tablet';
  hasCamera: boolean;
  isPWA: boolean;
  isMobilePWA: boolean; // New field to specifically detect mobile PWA
  isDesktopPWA: boolean; // New field to specifically detect desktop PWA
}

export interface PWAContextType {
  pwaState: PWAState;
  deviceInfo: DeviceInfo;
  triggerInstallPrompt: () => Promise<void>;
  resetInstallationStatus: () => void;
  recordInstallation: () => void;
  getAppStoreUrl: () => string;
  getPlayStoreUrl: () => string;
  updateServiceWorker: () => void;
  forceCameraDetection: () => Promise<boolean>;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

interface PWAProviderProps {
  children: ReactNode;
}

// Enhanced PWA detection for all platforms
const detectPWA = (): boolean => {
  // Enhanced PWA detection for desktop and mobile
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    // iOS Safari PWA detection
    (window.navigator.userAgent.includes('iPhone') &&
      window.navigator.userAgent.includes('Safari') &&
      !window.navigator.userAgent.includes('CriOS') &&
      !window.navigator.userAgent.includes('Mac OS X') &&
      (window.location.protocol === 'https:' ||
        window.location.hostname === 'localhost')) ||
    // Chrome PWA on macOS - multiple detection methods
    (window.navigator.userAgent.includes('Macintosh') &&
      window.navigator.userAgent.includes('Chrome') &&
      !window.navigator.userAgent.includes('Safari') &&
      window.location.protocol === 'https:') ||
    // Check if window dimensions suggest PWA mode (no browser UI)
    (window.navigator.userAgent.includes('Macintosh') &&
      window.outerWidth === window.screen.availWidth &&
      window.outerHeight === window.screen.availHeight) ||
    // Check if window height matches screen height (no browser chrome)
    (window.navigator.userAgent.includes('Macintosh') &&
      window.outerHeight === window.innerHeight) ||
    // Electron-based PWA detection
    (window.navigator.userAgent.includes('Macintosh') &&
      window.navigator.userAgent.includes('Chrome') &&
      window.navigator.userAgent.includes('Electron')); // ||
  // Simple fallback: if running in Chrome on macOS with HTTPS or localhost
  // (window.navigator.userAgent.includes('Macintosh') &&
  //   window.navigator.userAgent.includes('Chrome') &&
  //   (window.location.protocol === 'https:' ||
  //     window.location.hostname === 'localhost') &&
  //   window.outerWidth > 1000) || // Assume PWA if window is wide
  // // More aggressive fallback for development/testing
  // (window.navigator.userAgent.includes('Macintosh') &&
  //   window.navigator.userAgent.includes('Chrome') &&
  //   (window.location.protocol === 'https:' ||
  //     window.location.hostname === 'localhost')); // Any Chrome on macOS with HTTPS or localhost

  // Enhanced debugging with actual values
  const debugInfo = {
    userAgent: navigator.userAgent,
    displayMode: window.matchMedia('(display-mode: standalone)').matches,
    navigatorStandalone: (window.navigator as any).standalone,
    isMacintosh: window.navigator.userAgent.includes('Macintosh'),
    isChrome: window.navigator.userAgent.includes('Chrome'),
    isSafari: window.navigator.userAgent.includes('Safari'),
    actualBrowser: (() => {
      if (navigator.userAgent.includes('Chrome')) return 'Chrome';
      if (navigator.userAgent.includes('Safari')) return 'Safari';
      if (navigator.userAgent.includes('Firefox')) return 'Firefox';
      if (navigator.userAgent.includes('Edge')) return 'Edge';
      return 'unknown';
    })(),
    protocol: window.location.protocol,
    outerHeight: window.outerHeight,
    innerHeight: window.innerHeight,
    screenAvailHeight: window.screen.availHeight,
    screenAvailWidth: window.screen.availWidth,
    outerWidth: window.outerWidth,
    isStandalone,
    // Additional debug info
    windowMatch: window.outerWidth === window.screen.availWidth,
    heightMatch: window.outerHeight === window.screen.availHeight,
    hasElectron: window.navigator.userAgent.includes('Electron'),
    // Test individual conditions
    condition1: window.matchMedia('(display-mode: standalone)').matches,
    condition2: (window.navigator as any).standalone === true,
    condition3:
      window.navigator.userAgent.includes('Macintosh') &&
      window.navigator.userAgent.includes('Chrome') &&
      !window.navigator.userAgent.includes('Safari') &&
      window.location.protocol === 'https:',
    condition4:
      window.navigator.userAgent.includes('Macintosh') &&
      window.outerWidth === window.screen.availWidth &&
      window.outerHeight === window.screen.availHeight,
    condition5:
      window.navigator.userAgent.includes('Macintosh') &&
      window.outerHeight === window.innerHeight,
    condition6:
      window.navigator.userAgent.includes('Macintosh') &&
      window.navigator.userAgent.includes('Chrome') &&
      window.navigator.userAgent.includes('Electron'),
  };

  // console.log('PWA Detection Debug:', debugInfo);
  // console.log('PWA Detection Result:', isStandalone);

  return isStandalone;
};

// Device detection functions
const detectDeviceInfo = (): DeviceInfo => {
  const userAgent = navigator.userAgent;

  // Enhanced mobile detection using multiple methods
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) && !/Macintosh/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);

  // More comprehensive mobile detection
  const isMobile = (() => {
    // Method 1: User Agent detection
    const uaMobile =
      /Mobile|Android|iPhone|iPad|iPod/.test(userAgent) &&
      !/Mac OS X|Macintosh|Windows|Linux/.test(userAgent);

    // Method 2: Screen size detection
    const screenMobile =
      window.screen.width <= 768 || window.screen.height <= 768;

    // Method 3: Touch capability detection
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Method 4: Device pixel ratio (mobile devices typically have higher DPR)
    const highDPR = window.devicePixelRatio >= 2;

    // Method 5: Viewport detection
    const viewportMobile =
      window.innerWidth <= 768 || window.innerHeight <= 768;

    // Method 6: Platform detection
    const platformMobile =
      /iPhone|iPad|iPod|Android|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent,
      );

    // Combine all methods with weights
    const mobileScore = [
      uaMobile ? 3 : 0,
      screenMobile ? 2 : 0,
      hasTouch ? 2 : 0,
      highDPR ? 1 : 0,
      viewportMobile ? 2 : 0,
      platformMobile ? 3 : 0,
      isIOS ? 3 : 0,
      isAndroid ? 3 : 0,
    ].reduce((sum, score) => sum + score, 0);

    // Consider mobile if score is 4 or higher
    return mobileScore >= 4;
  })();

  const isTablet = /iPad|Android.*Tablet/.test(userAgent);
  const isDesktop = !isMobile;

  // Enhanced PWA detection with mobile/desktop differentiation
  const isPWA = (() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    const navigatorStandalone = (window.navigator as any).standalone === true;

    // Additional PWA indicators
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasManifest = !!document.querySelector('link[rel="manifest"]');
    const isHTTPS =
      window.location.protocol === 'https:' ||
      window.location.hostname === 'localhost';

    return (
      standalone ||
      navigatorStandalone ||
      (hasServiceWorker && hasManifest && isHTTPS)
    );
  })();

  // Extract OS version
  let osVersion = '';
  if (isIOS) {
    const match = userAgent.match(/OS (\d+_\d+)/);
    osVersion = match ? match[1].replace('_', '.') : '';
  } else if (isAndroid) {
    const match = userAgent.match(/Android (\d+\.\d+)/);
    osVersion = match ? match[1] : '';
  }

  // Detect browser
  let browser = 'unknown';
  if (userAgent.includes('Chrome')) {
    browser = 'Chrome';
  } else if (userAgent.includes('Safari')) {
    browser = 'Safari';
  } else if (userAgent.includes('Firefox')) {
    browser = 'Firefox';
  } else if (userAgent.includes('Edge')) {
    browser = 'Edge';
  }

  // Check PWA installation capability
  const canInstallPWA = 'serviceWorker' in navigator && 'PushManager' in window;

  // Check mobile app compatibility
  let canInstallMobileApp = false;
  let mobileAppCompatible = false;

  const MIN_IOS_VERSION = '14.0';
  const MIN_ANDROID_VERSION = '8.0';

  if (isIOS) {
    canInstallMobileApp = true;
    mobileAppCompatible = compareVersions(osVersion, MIN_IOS_VERSION) >= 0;
  } else if (isAndroid) {
    canInstallMobileApp = true;
    mobileAppCompatible = compareVersions(osVersion, MIN_ANDROID_VERSION) >= 0;
  }

  // Determine device type with enhanced logic
  let deviceType: 'ios' | 'android' | 'desktop' | 'tablet' = 'desktop';
  if (isIOS) {
    deviceType = isTablet ? 'tablet' : 'ios';
  } else if (isAndroid) {
    deviceType = isTablet ? 'tablet' : 'android';
  }

  // Check camera capabilities
  const hasCamera = (() => {
    try {
      return (
        'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices
      );
    } catch {
      return false;
    }
  })();

  // Enhanced debugging with comprehensive device information
  // const debugInfo = {
  //   userAgent,
  //   screen: {
  //     width: window.screen.width,
  //     height: window.screen.height,
  //     availWidth: window.screen.availWidth,
  //     availHeight: window.screen.availHeight,
  //   },
  //   window: {
  //     innerWidth: window.innerWidth,
  //     innerHeight: window.innerHeight,
  //     outerWidth: window.outerWidth,
  //     outerHeight: window.outerHeight,
  //   },
  //   devicePixelRatio: window.devicePixelRatio,
  //   touchSupport: {
  //     ontouchstart: 'ontouchstart' in window,
  //     maxTouchPoints: navigator.maxTouchPoints,
  //   },
  //   platform: navigator.platform,
  //   vendor: navigator.vendor,
  //   isMobile,
  //   isIOS,
  //   isAndroid,
  //   isDesktop,
  //   isTablet,
  //   deviceType,
  //   browser,
  //   isPWA,
  //   standalone: window.matchMedia('(display-mode: standalone)').matches,
  //   navigatorStandalone: (window.navigator as any).standalone,
  //   // Add service worker platform info to debug output
  //   serviceWorkerPlatform: (() => {
  //     try {
  //       const stored = localStorage.getItem('sw_platform_info');
  //       return stored ? JSON.parse(stored) : null;
  //     } catch {
  //       return null;
  //     }
  //   })(),
  // };

  // console.log('🔍 Enhanced Device Detection Debug:', debugInfo);

  return {
    isMobile,
    isIOS,
    isAndroid,
    isDesktop,
    isTablet,
    osVersion,
    browser,
    canInstallPWA,
    canInstallMobileApp,
    mobileAppCompatible,
    deviceType,
    hasCamera,
    isPWA,
    isMobilePWA: isPWA && isMobile,
    isDesktopPWA: isPWA && isDesktop,
  };
};

export const PWAProvider: React.FC<PWAProviderProps> = ({ children }) => {
  // Individual state variables for better reactivity
  const [isPWA, setIsPWA] = useState<boolean>(false);
  const [hasCamera, setHasCamera] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [isAndroid, setIsAndroid] = useState<boolean>(false);
  const [browser, setBrowser] = useState<string>('unknown');
  const [deviceType, setDeviceType] = useState<
    'ios' | 'android' | 'desktop' | 'tablet'
  >('desktop');

  const [pwaState, setPwaState] = useState<PWAState>({
    isInstalled: false,
    canInstall: false,
    installationDate: null,
    isStandalone: false,
    deferredPrompt: null,
    serviceWorkerUpdateAvailable: false,
  });

  // Initialize device detection on mount
  useEffect(() => {
    // console.log('🔍 Initializing device detection...');
    const initialDeviceInfo = detectDeviceInfo();
    // console.log('🔍 Initial deviceInfo:', initialDeviceInfo);

    setIsPWA(initialDeviceInfo.isPWA);
    setIsMobile(initialDeviceInfo.isMobile);
    setIsDesktop(initialDeviceInfo.isDesktop);
    setIsIOS(initialDeviceInfo.isIOS);
    setIsAndroid(initialDeviceInfo.isAndroid);
    setBrowser(initialDeviceInfo.browser);
    setDeviceType(initialDeviceInfo.deviceType);

    // console.log('🔍 Set state variables:', {
    //   isPWA: initialDeviceInfo.isPWA,
    //   isMobile: initialDeviceInfo.isMobile,
    //   isDesktop: initialDeviceInfo.isDesktop,
    //   isIOS: initialDeviceInfo.isIOS,
    //   isAndroid: initialDeviceInfo.isAndroid,
    //   browser: initialDeviceInfo.browser,
    //   deviceType: initialDeviceInfo.deviceType,
    // });
  }, []);

  // Update PWA state when isPWA changes
  useEffect(() => {
    // console.log('🔄 PWA state updated, isPWA:', isPWA);
    setPwaState((prev) => ({
      ...prev,
      isStandalone: isPWA,
    }));
  }, [isPWA]);

  // Force re-evaluation of PWA detection when camera status changes
  useEffect(() => {
    // console.log('🔄 Camera effect triggered, hasCamera:', hasCamera);
    if (hasCamera) {
      // console.log('🔄 Camera detected, re-evaluating PWA detection...');
      // Re-run PWA detection when camera is available
      const newIsPWA = detectPWA();
      // console.log('🔄 PWA detection result:', newIsPWA);
      setIsPWA(newIsPWA);
    }
  }, [hasCamera]);

  // Force camera detection when PWA is detected
  useEffect(() => {
    // console.log(
    //   '🔄 PWA state effect triggered, isStandalone:',
    //   pwaState.isStandalone,
    //   'hasCamera:',
    //   hasCamera,
    // );
    if (pwaState.isStandalone && !hasCamera) {
      // console.log('🔄 PWA detected, forcing camera detection...');
      // Trigger immediate camera detection
      const forceDetect = async () => {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const hasVideoDevices = devices.some(
            (device) => device.kind === 'videoinput',
          );
          if (hasVideoDevices) {
            // console.log('✅ Camera found via PWA trigger');
            setHasCamera(true);
          }
        } catch (error) {
          console.log('❌ PWA camera detection failed:', error);
        }
      };
      forceDetect();
    }
  }, [pwaState.isStandalone, hasCamera]);

  // Check if PWA is installed using multiple methods
  const checkIfPWAInstalled = (): boolean => {
    // Check if currently running in standalone mode using centralized detection
    if (detectPWA()) {
      return true;
    }

    // Check if installation was previously recorded
    const wasInstalled = localStorage.getItem('pwa-installed') === 'true';

    if (wasInstalled) {
      // If we think it was installed, verify it's still available
      return verifyInstallationStatus();
    }

    return false;
  };

  // Verify if PWA installation is still valid
  const verifyInstallationStatus = (): boolean => {
    // Check if the PWA is still available for installation
    // This helps detect if the PWA was uninstalled

    // Method 1: Check if beforeinstallprompt event is available
    // If the event is available, the PWA can be installed (meaning it's not currently installed)
    const canInstall = localStorage.getItem('pwa-can-install') === 'true';

    // Method 2: Check if we're in a browser context (not standalone) using centralized detection
    const isStandalone = detectPWA();

    // If we're not in standalone mode and the app can be installed,
    // it likely means the PWA was uninstalled
    if (!isStandalone && canInstall) {
      // Clear the installation record since PWA appears to be uninstalled
      clearInstallationRecord();
      return false;
    }

    return true; // Assume still installed if we can't detect uninstallation
  };

  // Clear installation record
  const clearInstallationRecord = (): void => {
    localStorage.removeItem('pwa-installed');
    localStorage.removeItem('pwa-installed-date');
    localStorage.removeItem('pwa-can-install');
  };

  // Record installation
  const recordInstallation = (): void => {
    localStorage.setItem('pwa-installed', 'true');
    localStorage.setItem('pwa-installed-date', new Date().toISOString());

    setPwaState((prev) => ({
      ...prev,
      isInstalled: true,
      installationDate: new Date(),
    }));
  };

  // Reset installation status
  const resetInstallationStatus = (): void => {
    clearInstallationRecord();
    setPwaState((prev) => ({
      ...prev,
      isInstalled: false,
      installationDate: null,
      canInstall: true,
    }));
  };

  // Update service worker to force cache refresh
  const updateServiceWorker = (): void => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.update();
        });
      });
    }
  };

  // Trigger install prompt
  const triggerInstallPrompt = async (): Promise<void> => {
    if (pwaState.deferredPrompt) {
      pwaState.deferredPrompt.prompt();
      recordInstallation();
      const choiceResult = await pwaState.deferredPrompt.userChoice;

      if (choiceResult.outcome === 'accepted') {
        recordInstallation();
      } else {
        clearInstallationRecord();
      }

      setPwaState((prev) => ({
        ...prev,
        deferredPrompt: null,
      }));
    } else {
      console.log('No install prompt available');
    }
  };

  useEffect(() => {
    // Check initial state using centralized PWA detection
    const isStandalone = detectPWA();
    const isInstalled = checkIfPWAInstalled();
    const canInstall = localStorage.getItem('pwa-can-install') === 'true';
    const installationDateStr = localStorage.getItem('pwa-installed-date');
    const installationDate = installationDateStr
      ? new Date(installationDateStr)
      : null;

    setPwaState({
      isInstalled,
      canInstall,
      installationDate,
      isStandalone,
      deferredPrompt: null,
      serviceWorkerUpdateAvailable: false,
    });

    // Listen for app installation event
    const handleAppInstalled = () => {
      recordInstallation();
    };

    // Listen for beforeinstallprompt to track installability
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the default browser install prompt
      e.preventDefault();

      // Store the event for later use
      setPwaState((prev) => ({
        ...prev,
        deferredPrompt: e,
        canInstall: true,
      }));

      // Store that the app can be installed
      localStorage.setItem('pwa-can-install', 'true');
    };

    // Service worker update detection
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (
                newWorker.state === 'installed' &&
                navigator.serviceWorker.controller
              ) {
                // New service worker is available
                setPwaState((prev) => ({
                  ...prev,
                  serviceWorkerUpdateAvailable: true,
                }));
              }
            });
          }
        });
      });
    }

    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Cleanup
    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  // Manual camera detection function
  const forceCameraDetection = async (): Promise<boolean> => {
    // console.log('🔧 Force camera detection requested...');
    try {
      if (
        !('mediaDevices' in navigator) ||
        !('getUserMedia' in navigator.mediaDevices)
      ) {
        console.log('❌ MediaDevices API not available');
        return false;
      }

      // Force camera access request
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // console.log('✅ Camera access granted via force detection');
      stream.getTracks().forEach((track) => track.stop());
      setHasCamera(true);
      return true;
    } catch (error) {
      console.log('❌ Force camera detection failed:', error);
      setHasCamera(false);
      return false;
    }
  };

  // Create computed deviceInfo from individual state variables
  // console.log('🔍 Creating deviceInfo with state:', {
  //   isMobile,
  //   isIOS,
  //   isAndroid,
  //   isDesktop,
  //   browser,
  //   deviceType,
  //   hasCamera,
  //   isPWA,
  // });

  const deviceInfo: DeviceInfo = {
    isMobile,
    isIOS,
    isAndroid,
    isDesktop,
    isTablet: deviceType === 'tablet',
    osVersion: '', // Will be computed if needed
    browser,
    canInstallPWA: 'serviceWorker' in navigator && 'PushManager' in window,
    canInstallMobileApp: isIOS || isAndroid,
    mobileAppCompatible: isIOS || isAndroid, // Simplified for now
    deviceType,
    hasCamera,
    isPWA,
    // Use enhanced client-side detection only
    isMobilePWA: isPWA && isMobile,
    isDesktopPWA: isPWA && isDesktop,
  };

  // console.log('🔍 Enhanced Client-Side Platform Detection:', {
  //   isMobile,
  //   isIOS,
  //   isAndroid,
  //   isDesktop,
  //   deviceType,
  //   isPWA,
  //   isMobilePWA: deviceInfo.isMobilePWA,
  //   isDesktopPWA: deviceInfo.isDesktopPWA,
  // });

  // Log platform detection source and results
  // console.log('🔍 Platform Detection Source:', 'client-side');
  // console.log('🔍 Reliable Platform Detection:', {
  //   isMobile: isMobile,
  //   isIOS: isIOS,
  //   isAndroid: isAndroid,
  //   isDesktop: isDesktop,
  //   platform: deviceType,
  //   source: 'client-side',
  // });

  // console.log('🔍 Created deviceInfo:', deviceInfo);

  const value: PWAContextType = {
    pwaState,
    deviceInfo,
    triggerInstallPrompt,
    resetInstallationStatus,
    recordInstallation,
    getAppStoreUrl,
    getPlayStoreUrl,
    updateServiceWorker,
    forceCameraDetection,
  };

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
};

// Custom hook for device detection
export const useDeviceDetection = (): DeviceInfo => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useDeviceDetection must be used within a PWAProvider');
  }
  return context.deviceInfo;
};

// Custom hook to use PWA context
export const usePWA = (): PWAContextType => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

// Custom hook for PWA detection
export const useIsPWA = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useIsPWA must be used within a PWAProvider');
  }
  return context.deviceInfo.isPWA;
};

// Custom hook for camera detection
export const useCamera = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useCamera must be used within a PWAProvider');
  }
  const hasCamera = context.deviceInfo.hasCamera;
  // console.log('🔍 useCamera hook returning:', hasCamera);
  return hasCamera;
};

// Custom hook for mobile detection
export const useMobile = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useMobile must be used within a PWAProvider');
  }
  return context.deviceInfo.isMobile;
};

// Custom hook for desktop detection
export const useDesktop = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useDesktop must be used within a PWAProvider');
  }
  return context.deviceInfo.isDesktop;
};

// Custom hook for iOS detection
export const useIOS = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useIOS must be used within a PWAProvider');
  }
  return context.deviceInfo.isIOS;
};

// Custom hook for Android detection
export const useAndroid = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useAndroid must be used within a PWAProvider');
  }
  return context.deviceInfo.isAndroid;
};

// Custom hook for browser detection
export const useBrowser = (): string => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useBrowser must be used within a PWAProvider');
  }
  return context.deviceInfo.browser;
};

// Custom hook for PWA installation capability
export const useCanInstallPWA = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useCanInstallPWA must be used within a PWAProvider');
  }
  return context.deviceInfo.canInstallPWA;
};

// Custom hook for mobile app installation capability
export const useCanInstallMobileApp = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useCanInstallMobileApp must be used within a PWAProvider');
  }
  return context.deviceInfo.canInstallMobileApp;
};

// Custom hook for mobile app compatibility
export const useMobileAppCompatible = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useMobileAppCompatible must be used within a PWAProvider');
  }
  return context.deviceInfo.mobileAppCompatible;
};

// Custom hook for device type
export const useDeviceType = (): 'ios' | 'android' | 'desktop' | 'tablet' => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useDeviceType must be used within a PWAProvider');
  }
  return context.deviceInfo.deviceType;
};

// Custom hook for OS version
export const useOSVersion = (): string => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useOSVersion must be used within a PWAProvider');
  }
  return context.deviceInfo.osVersion;
};

// New hooks for mobile/desktop PWA detection
export const useIsMobilePWA = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useIsMobilePWA must be used within a PWAProvider');
  }
  return context.deviceInfo.isMobilePWA;
};

export const useIsDesktopPWA = (): boolean => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('useIsDesktopPWA must be used within a PWAProvider');
  }
  return context.deviceInfo.isDesktopPWA;
};

// Helper function to compare versions
const compareVersions = (version1: string, version2: string): number => {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);

  const maxLength = Math.max(v1Parts.length, v2Parts.length);

  for (let i = 0; i < maxLength; i++) {
    const v1 = v1Parts[i] || 0;
    const v2 = v2Parts[i] || 0;

    if (v1 > v2) return 1;
    if (v1 < v2) return -1;
  }

  return 0;
};

// App store URLs
export const getAppStoreUrl = (): string => {
  return 'https://apps.apple.com/us/app/embue-super-3-0/id1673479606';
};

export const getPlayStoreUrl = (): string => {
  return 'https://play.google.com/store/apps/details?id=com.embue.superapp';
};
